import { ChangeEvent, useEffect, useState } from 'react';
import clsx from 'clsx';

export interface TimeInputProps {
  value: number;
  onChange: (seconds: number) => void;
  showHours?: boolean;
  disabled?: boolean;
}

const cell =
  'w-14 rounded-lg border border-gray-300 bg-white px-2 py-2 text-center text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100';

const TimeInput = ({ value, onChange, showHours = false, disabled }: TimeInputProps) => {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const totalSeconds = Math.floor(value);
    if (showHours) {
      setHours(Math.floor(totalSeconds / 3600));
      setMinutes(Math.floor((totalSeconds % 3600) / 60));
    } else {
      setMinutes(Math.floor(totalSeconds / 60));
    }
    setSeconds(totalSeconds % 60);
  }, [value, showHours]);

  const emit = (h: number, m: number, s: number) =>
    onChange(showHours ? h * 3600 + m * 60 + s : m * 60 + s);

  const handleHours = (e: ChangeEvent<HTMLInputElement>) => {
    const h = Math.max(0, parseInt(e.target.value) || 0);
    setHours(h);
    emit(h, minutes, seconds);
  };
  const handleMinutes = (e: ChangeEvent<HTMLInputElement>) => {
    const m = Math.max(0, Math.min(showHours ? 59 : 999, parseInt(e.target.value) || 0));
    setMinutes(m);
    emit(hours, m, seconds);
  };
  const handleSeconds = (e: ChangeEvent<HTMLInputElement>) => {
    const s = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
    setSeconds(s);
    emit(hours, minutes, s);
  };

  return (
    <div className="flex items-center gap-1">
      {showHours && (
        <>
          <input type="number" value={hours} onChange={handleHours} min={0} max={23} placeholder="h" disabled={disabled} className={clsx(cell)} />
          <span className="text-gray-400">:</span>
        </>
      )}
      <input
        type="number"
        value={minutes.toString().padStart(2, '0')}
        onChange={handleMinutes}
        min={0}
        max={showHours ? 59 : 999}
        placeholder="min"
        disabled={disabled}
        className={clsx(cell)}
      />
      <span className="text-gray-400">:</span>
      <input
        type="number"
        value={seconds.toString().padStart(2, '0')}
        onChange={handleSeconds}
        min={0}
        max={59}
        placeholder="sec"
        disabled={disabled}
        className={clsx(cell)}
      />
    </div>
  );
};

export default TimeInput;
